/* eslint-disable no-unused-vars */
import { useCallback, useMemo } from 'react';
import { IconButton, ParagraphWithTooltip, Buttons } from 'components';
import { DeleteIcon } from 'assets/icons';
import * as S from './FilterSelectedItems.styled';
import ReactCountryFlag from 'react-country-flag';
import { useSignal } from '@preact/signals-react';

const MAX_ITEMS = 6;
const maxItems = MAX_ITEMS;

export const FilterSelectedItems = ({
  selectedAppsAndCampaigns,
  deleteSelectedItem,
  selectedStatusFilter$,
  selectedTypeFilter$,
  selectedAppTypeFilter$,
  deleteSelectedStatusFilter,
  deleteSelectedTypeFilter,
  deleteSelectedAppTypeFilter,
  selectedCountriesFilter$,
  all_countries,
  handleDeselectCountry,
  clearAll,
  statuses,
  types,
  appTypes,
  appNumberToAppName,
  campNumberToCampName,
}) => {
  const { selectedApps, selectedCampaigns } = selectedAppsAndCampaigns;

  const noSearching = useMemo(() => {
    if (!selectedApps.length && !selectedCampaigns.length) return true;
    return false;
  }, [selectedApps, selectedCampaigns]);

  const isShowMore$ = useSignal(false);

  const appTypeFilterValue = selectedAppTypeFilter$?.value ?? 'allAppTypes';

  const filtersCounter = useMemo(() => {
    let counter = 0;
    if (selectedStatusFilter$.value !== 'allStatuses' && selectedStatusFilter$.value !== 'empty')
      ++counter;
    if (selectedTypeFilter$.value !== 'allStatuses' && selectedTypeFilter$.value !== 'empty')
      ++counter;
    if (appTypeFilterValue !== 'allAppTypes') ++counter;
    if (selectedApps.length) counter += selectedApps.length;
    if (selectedCampaigns.length) counter += selectedCampaigns.length;
    if (selectedCountriesFilter$.value.length) counter += selectedCountriesFilter$.value.length;
    return counter;
  }, [
    selectedStatusFilter$.value,
    selectedTypeFilter$.value,
    appTypeFilterValue,
    selectedApps,
    selectedCampaigns,
    selectedCountriesFilter$.value,
  ]);

  const createArray = () => {
    const res = [];
    if (selectedStatusFilter$.value !== 'allStatuses' && selectedStatusFilter$.value !== 'empty') {
      const newItem = { type: 'status', value: selectedStatusFilter$.value };
      res.push(newItem);
    }
    if (selectedTypeFilter$.value !== 'allStatuses' && selectedTypeFilter$.value !== 'empty') {
      const newItem = { type: 'type', value: selectedTypeFilter$.value };
      res.push(newItem);
    }
    if (appTypeFilterValue !== 'allAppTypes') {
      const newItem = { type: 'appType', value: appTypeFilterValue };
      res.push(newItem);
    }
    if (selectedApps.length) {
      selectedApps.forEach((appNumber) => {
        const newItem = { type: 'selectedApps', value: appNumber };
        res.push(newItem);
      });
    }
    if (selectedCampaigns.length) {
      selectedCampaigns.forEach((campNumber) => {
        const newItem = { type: 'selectedCampaigns', value: campNumber };
        res.push(newItem);
      });
    }
    if (selectedCountriesFilter$.value.length) {
      selectedCountriesFilter$.value.forEach((countryName) => {
        const newItem = { type: 'countries', value: countryName };
        res.push(newItem);
      });
    }
    return res;
  };

  const fullItems = createArray();
  const elementsToShow = isShowMore$.value ? fullItems : fullItems.slice(0, maxItems);

  const textStyle = { fontSize: '12px', color: '#334155' };

  return (
    <S.SelectedContainer
      isHidden={
        noSearching &&
        (selectedStatusFilter$.value === 'allStatuses' ||
          selectedStatusFilter$.value === 'empty') &&
        (selectedTypeFilter$.value === 'allTypes' || selectedTypeFilter$.value === 'empty') &&
        appTypeFilterValue === 'allAppTypes' &&
        !selectedCountriesFilter$.value.length
      }
    >
      <S.ItemsContent>
        {elementsToShow?.map(({ type, value }) => {
          if (type === 'status') {
            return (
              <S.SelectedItem key={value}>
                <ParagraphWithTooltip style={textStyle} text={`Status: ${statuses[value]}`} />
                <IconButton.SmallSize
                  style={{ minWidth: '32px' }}
                  onClick={deleteSelectedStatusFilter}
                  icon={DeleteIcon}
                  type={IconButton.btnTypes.tertiary}
                  isDefaultMuted
                />
              </S.SelectedItem>
            );
          }
          if (type === 'type') {
            return (
              <S.SelectedItem key={value}>
                <ParagraphWithTooltip style={textStyle} text={`Type: ${types[value]}`} />
                <IconButton.SmallSize
                  style={{ minWidth: '32px' }}
                  onClick={deleteSelectedTypeFilter}
                  icon={DeleteIcon}
                  type={IconButton.btnTypes.tertiary}
                  isDefaultMuted
                />
              </S.SelectedItem>
            );
          }
          if (type === 'appType') {
            return (
              <S.SelectedItem key={value}>
                <ParagraphWithTooltip
                  style={textStyle}
                  text={`Application Type: ${appTypes?.[value] ?? value}`}
                />
                <IconButton.SmallSize
                  style={{ minWidth: '32px' }}
                  onClick={deleteSelectedAppTypeFilter}
                  icon={DeleteIcon}
                  type={IconButton.btnTypes.tertiary}
                  isDefaultMuted
                />
              </S.SelectedItem>
            );
          }
          if (type === 'selectedApps') {
            return (
              <S.SelectedItem key={value}>
                <ParagraphWithTooltip
                  style={textStyle}
                  text={`App: ${appNumberToAppName[value]}`}
                />
                <IconButton.SmallSize
                  style={{ minWidth: '32px' }}
                  onClick={() => deleteSelectedItem({ appNumber: value })}
                  icon={DeleteIcon}
                  type={IconButton.btnTypes.tertiary}
                  isDefaultMuted
                />
              </S.SelectedItem>
            );
          }
          if (type === 'selectedCampaigns') {
            return (
              <S.SelectedItem key={value}>
                <ParagraphWithTooltip
                  style={textStyle}
                  text={`Campaign: ${campNumberToCampName[value]}`}
                />
                <IconButton.SmallSize
                  style={{ minWidth: '32px' }}
                  onClick={() => deleteSelectedItem({ campNumber: value })}
                  icon={DeleteIcon}
                  type={IconButton.btnTypes.tertiary}
                  isDefaultMuted
                />
              </S.SelectedItem>
            );
          }
          if (type === 'countries') {
            return (
              <S.SelectedItem key={value}>
                <S.Paragraph>GEO:</S.Paragraph>
                <ReactCountryFlag
                  countryCode={value}
                  svg
                  style={{
                    width: '20px',
                    height: '15px',
                    marginRight: '8px',
                  }}
                />
                <ParagraphWithTooltip
                  style={textStyle}
                  text={`${all_countries?.[value] ?? ''} - ${value}`}
                />
                <IconButton.SmallSize
                  style={{ minWidth: '32px' }}
                  onClick={() => handleDeselectCountry(value)}
                  icon={DeleteIcon}
                  type={IconButton.btnTypes.tertiary}
                  isDefaultMuted
                />
              </S.SelectedItem>
            );
          }
        })}
        {filtersCounter > maxItems && isShowMore$.value && (
          <Buttons.TertiarySmall
            id='showMoreButton'
            onClick={() => {
              isShowMore$.value = !isShowMore$.value;
            }}
          >
            {isShowMore$.value ? 'Show Less' : `+ ${filtersCounter - maxItems}`}
          </Buttons.TertiarySmall>
        )}
      </S.ItemsContent>
      {filtersCounter > maxItems && !isShowMore$.value && (
        <Buttons.TertiarySmall
          id='showMoreButton'
          onClick={() => {
            isShowMore$.value = !isShowMore$.value;
          }}
        >
          {isShowMore$.value ? 'Show Less' : `+ ${filtersCounter - maxItems}`}
        </Buttons.TertiarySmall>
      )}
      <Buttons.QuaternaryNormal
        id='showMoreButton'
        style={{ position: 'absolute', right: '16px', top: '12px' }}
        onClick={clearAll}
      >
        Clear All Filters
      </Buttons.QuaternaryNormal>
    </S.SelectedContainer>
  );
};
